import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { addUser } from "../../redux/userSlice";
import { getHouseList } from "../../redux/houseSettingsSlice";
import { notification, Form, Input, Select, Button, Row, Col } from "antd";
import { NepaliDatePicker } from "nepali-datepicker-reactjs";
import "nepali-datepicker-reactjs/dist/index.css";
import { ADToBS } from "bikram-sambat-js";
import { convertToEnglishDate } from "../../components/FormatDate";

const { Option } = Select;

const AddUser = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { loading } = useSelector((state) => state.user);
  const { houseSettings } = useSelector((state) => state.houseSettings);

  const [form] = Form.useForm();
  const [leaseStartDate, setLeaseStartDate] = useState("");
  const [media, setMedia] = useState(null);

  // Set default lease start date to today in BS
  useEffect(() => {
    try {
      const today = new Date();
      const nepaliDate = ADToBS(today.toISOString().split("T")[0]);
      setLeaseStartDate(nepaliDate);
      form.setFieldsValue({ start_date: nepaliDate });
    } catch (error) {
      console.error("Error converting AD to BS:", error);
    }
  }, [form]);

  useEffect(() => {
    dispatch(getHouseList());
  }, [dispatch]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      setMedia(e.target.files[0]);
    }
  };

  const onFinish = (values) => {
    const formData = new FormData();
    formData.append("house_id", values.house_id);
    formData.append("type", values.user_type);
    formData.append("name", values.name);
    formData.append("phone_number", values.phone_number);
    formData.append("email", values.email);
    formData.append("password", values.password);
    formData.append("confirm_password", values.confirm_password);
    formData.append("status", "1");
    if (values.emergency_contact)
      formData.append("emergency_contact", values.emergency_contact);
    if (values.secondary_phone_number)
      formData.append("secondary_phone_number", values.secondary_phone_number);
    if (values.citizenship_number)
      formData.append("citizenship_number", values.citizenship_number);
    if (values.citizenship_district)
      formData.append("citizenship_district", values.citizenship_district);
    if (media) formData.append("document", media);
    if (leaseStartDate) formData.append("start_date", convertToEnglishDate(leaseStartDate));
    if (values.notes) formData.append("notes", values.notes);

    dispatch(addUser(formData))
      .unwrap()
      .then(() => {
        notification.success({
          message: "Success",
          description: "User added successfully",
        });
        navigate("/dashboard/userlist");
      })
      .catch((err) => {
        notification.error({
          message: "Error",
          description: err?.message || "Failed to add user",
        });
      });
  };

  return (
    <div className="pb-20 mx-6">
      <h3 className="font-bold text-[22px] mb-4">Add User</h3>
      <div className="bg-gray-300 rounded-lg p-4">
        <div className="py-6 bg-white rounded-lg m-3 p-6">
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
          >
            {/* User Type and House Number - Row 1 */}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="User Type"
                  name="user_type"
                  rules={[{ required: true, message: "Please select user type!" }]}
                >
                  <Select placeholder="Select User Type">
                    <Option value="tenant">Tenant</Option>
                    <Option value="owner">Owner</Option>
                  </Select>
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="House Number"
                  name="house_id"
                  rules={[{ required: true, message: "Please select a house number!" }]}
                >
                  <Select placeholder="Select House Number" loading={!houseSettings}>
                    {houseSettings?.map((house) => (
                      <Option key={house.id} value={house.id}>
                        {house.house_number}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            {/* Name and Email - Row 2 */}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Name"
                  name="name"
                  rules={[
                    { required: true, message: "Please enter name!" },
                    { min: 2, message: "Name must be at least 2 characters!" },
                  ]}
                >
                  <Input placeholder="Enter full name" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[
                    { required: true, message: "Please enter email!" },
                    { type: "email", message: "Please enter a valid email!" },
                  ]}
                >
                  <Input placeholder="example@domain.com" />
                </Form.Item>
              </Col>
            </Row>

            {/* Phone Number and Secondary Phone Number - Row 3 */}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Phone Number"
                  name="phone_number"
                  rules={[
                    { required: true, message: "Please enter phone number!" },
                    {
                      pattern: /^[0-9]{10}$/,
                      message: "Phone number must be exactly 10 digits!",
                    },
                  ]}
                >
                  <Input placeholder="98XXXXXXXX" maxLength={10} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Secondary Phone Number (Optional)"
                  name="secondary_phone_number"
                  rules={[
                    {
                      pattern: /^[0-9]{10}$/,
                      message: "Phone number must be exactly 10 digits!",
                    },
                  ]}
                >
                  <Input placeholder="98XXXXXXXX" maxLength={10} />
                </Form.Item>
              </Col>
            </Row>

            {/* Emergency Contact and Citizenship Number - Row 4 */}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Emergency Contact (Optional)"
                  name="emergency_contact"
                  rules={[
                    {
                      pattern: /^[0-9]{10}$/,
                      message: "Emergency contact must be 10 digits!",
                    },
                  ]}
                >
                  <Input placeholder="98XXXXXXXX" maxLength={10} />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Citizenship Number (Optional)"
                  name="citizenship_number"
                  rules={[
                    {
                      pattern: /^[0-9]{2}-[0-9]{2}-[0-9]{2}-[0-9]{5}$/,
                      message: "Format: XX-XX-XX-XXXXX (e.g., 01-75-12-12345)",
                    },
                  ]}
                >
                  <Input placeholder="01-75-12-12345" maxLength={17} />
                </Form.Item>
              </Col>
            </Row>

            {/* Citizenship District - Row 5 */}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Citizenship District (Optional)"
                  name="citizenship_district"
                >
                  <Input placeholder="Enter district name" />
                </Form.Item>
              </Col>
            </Row>

            {/* Password and Confirm Password - Row 6 */}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Password"
                  name="password"
                  rules={[
                    { required: true, message: "Please enter password!" },
                    { min: 6, message: "Password must be at least 6 characters!" },
                  ]}
                >
                  <Input.Password placeholder="Enter password" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Confirm Password"
                  name="confirm_password"
                  dependencies={["password"]}
                  rules={[
                    { required: true, message: "Please confirm password!" },
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        if (!value || getFieldValue("password") === value) {
                          return Promise.resolve();
                        }
                        return Promise.reject(
                          new Error("Passwords do not match!")
                        );
                      },
                    }),
                  ]}
                >
                  <Input.Password placeholder="Confirm password" />
                </Form.Item>
              </Col>
            </Row>

            {/* Lease Start Date - Row 7 */}
            <Row gutter={16}>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Lease Start Date"
                  name="start_date"
                  rules={[{ required: true, message: "Please select start date!" }]}
                >
                  <NepaliDatePicker
                    value={leaseStartDate}
                    options={{ calenderLocale: "en", valueLocale: "en" }}
                    onChange={(value) => {
                      setLeaseStartDate(value);
                      form.setFieldsValue({ start_date: value });
                    }}
                    className="w-full custom-date-picker"
                    placeholder="YYYY-MM-DD"
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Image Upload - Row 8 */}
            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item label="Image (Optional)">
                  <input
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    onChange={handleFileChange}
                    className="block w-full text-sm text-gray-900 border border-gray-300 rounded-lg cursor-pointer bg-gray-50 focus:outline-none p-2"
                  />
                </Form.Item>
              </Col>
            </Row>

            {/* Notes - Row 9 */}
            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item label="Notes (Optional)" name="notes">
                  <Input.TextArea rows={4} placeholder="Additional notes..." />
                </Form.Item>
              </Col>
            </Row>

            {/* Submit Button */}
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={loading}
                className="w-full bg-[#403F93] hover:bg-[#2d2d7a]"
                size="large"
              >
                {loading ? "Adding..." : "Add User"}
              </Button>
            </Form.Item>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default AddUser;